import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  #freeShippingThreshold = 4900;
  #shippingFee = 699;

  #recIndex = 0;
  #recProducts = [];
  #recCache = new Map();

  #updateTimer = null;
  #isOwnEvent = false;
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    if (this.#updateTimer) clearTimeout(this.#updateTimer);
    this.#observer?.disconnect();
  }

  #handleCartAdd = () => {
    if (this.#isOwnEvent) return;
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleCartUpdate = (event) => {
    if (this.#isOwnEvent) return;
    if (this.#updateTimer) clearTimeout(this.#updateTimer);
    this.#updateTimer = setTimeout(() => {
      const cart = event?.detail?.data;
      if (typeof cart?.total_price === 'number' && !isNaN(cart.total_price)) {
        this.#updateUI(cart);
      } else {
        this.#fetchAndUpdate();
      }
    }, 600);
  };

  open() {
    this.showDialog();
    requestAnimationFrame(() => {
      this.#fetchAndUpdate();
      this.#startObserving();
    });

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.closeDialog();
  }

  #startObserving() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    this.#observer?.disconnect();

    const content = dialog.querySelector('scroll-hint.cart-drawer__content');
    if (!content) return;

    this.#observer = new MutationObserver(() => {
      this.#ensureShippingFeeEl();
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => this.#updateUI(cart))
        .catch(() => {});
    });

    this.#observer.observe(content, {
      childList: true,
      subtree: false,
    });
  }

  #fetchAndUpdate = async () => {
    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      this.#updateUI(cart);
      const firstItem = cart.items?.[0];
      if (firstItem?.product_id) {
        this.#fetchAndRenderRecommendations(firstItem.product_id, cart.items);
      }
    } catch (e) {
      console.warn('[CartDrawer] 无法获取购物车数据', e);
    }
  };

  #updateUI(cart) {
    if (typeof cart?.total_price !== 'number' || isNaN(cart.total_price)) return;
    this.#updateProgressBar(cart);
    this.#renderShippingFee(cart.total_price);
  }

  #fetchAndRenderRecommendations = async (productId, cartItems = []) => {
    try {
      let products;
      if (this.#recCache.has(productId)) {
        products = this.#recCache.get(productId);
      } else {
        const res = await fetch(
          `/recommendations/products.json?product_id=${productId}&limit=10&intent=related`
        );
        const data = await res.json();
        products = data.products ?? [];
        this.#recCache.set(productId, products);
      }

      const cartProductIds = new Set(cartItems.map(item => item.product_id));
      const filtered = products.filter(p => {
        const inCart = cartProductIds.has(p.id);
        const hasAvailable = p.variants?.some(v => v.available);
        return !inCart && hasAvailable;
      });

      this.#renderRecommendations(filtered);
    } catch (e) {
      console.warn('[CartDrawer] 推荐加载失败', e);
    }
  };

  // ─── Free Shipping Progress Bar ───────────────────────────────────────────

  #ensureFreeShippingEl() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return null;

    let el = dialog.querySelector('.cart-drawer__free-shipping');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cart-drawer__free-shipping';
      el.innerHTML = `
        <p class="free-shipping__message">
          <span class="free-shipping__message--progress">
            Add <strong class="free-shipping__remain"></strong> more to unlock free shipping 🚚
          </span>
          <span class="free-shipping__message--reached" hidden>
            🎉 You've unlocked <strong>free shipping</strong>!
          </span>
        </p>
        <div class="free-shipping__bar-wrap">
          <div class="free-shipping__bar-fill"></div>
          <div class="free-shipping__bar-indicator"></div>
        </div>
      `;

      const header = dialog.querySelector('.cart-drawer__header');
      if (header) {
        header.after(el);
      } else {
        dialog.prepend(el);
      }

      this.#injectStyles();
    }

    return el;
  }

  #updateProgressBar(cart) {
    const el = this.#ensureFreeShippingEl();
    if (!el) return;

    const total = cart.total_price;
    const threshold = this.#freeShippingThreshold;
    const pct = Math.min((total / threshold) * 100, 100);
    const remain = Math.max(threshold - total, 0);

    const fill = el.querySelector('.free-shipping__bar-fill');
    const indicator = el.querySelector('.free-shipping__bar-indicator');
    const msgProgress = el.querySelector('.free-shipping__message--progress');
    const msgReached = el.querySelector('.free-shipping__message--reached');
    const remainEl = el.querySelector('.free-shipping__remain');

    if (fill) fill.style.width = pct + '%';
    if (indicator) indicator.style.left = pct + '%';

    if (msgProgress && msgReached) {
      if (remain <= 0) {
        msgProgress.hidden = true;
        msgReached.hidden = false;
        fill?.classList.add('free-shipping__bar-fill--reached');
      } else {
        msgProgress.hidden = false;
        msgReached.hidden = true;
        fill?.classList.remove('free-shipping__bar-fill--reached');
        if (remainEl) remainEl.textContent = this.#formatMoney(remain, cart.currency);
      }
    }
  }

  // ─── Recommendations ───────────────────────────────────────────────────────

  #ensureRecommendationsEl() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return null;

    let el = dialog.querySelector('.cart-drawer__recommendations');

    if (!el) {
      el = document.createElement('div');
      el.className = 'cart-drawer__recommendations';
      el.innerHTML = `
        <div class="rec__header">
          <p class="rec__title">You may also like</p>
          <div class="rec__nav">
            <button class="rec__nav-btn rec__nav-prev" aria-label="Previous">&#8249;</button>
            <button class="rec__nav-btn rec__nav-next" aria-label="Next">&#8250;</button>
          </div>
        </div>
        <div class="rec__card">
          <a class="rec__img-link" href="#">
            <div class="rec__img-wrap">
              <img class="rec__img" src="" alt="" loading="lazy">
            </div>
          </a>
          <div class="rec__info">
            <a class="rec__name" href="#"></a>
            <span class="rec__price"></span>
          </div>
          <button class="rec__add-btn" data-variant-id="">
            <span>+</span> Add
          </button>
        </div>
      `;

      const content = dialog.querySelector('scroll-hint.cart-drawer__content');
      if (content) {
        content.after(el);
      } else {
        dialog.append(el);
      }

      el.querySelector('.rec__nav-prev')?.addEventListener('click', () => {
        this.#recIndex = (this.#recIndex - 1 + this.#recProducts.length) % this.#recProducts.length;
        this.#renderRecCard();
      });

      el.querySelector('.rec__nav-next')?.addEventListener('click', () => {
        this.#recIndex = (this.#recIndex + 1) % this.#recProducts.length;
        this.#renderRecCard();
      });

      el.querySelector('.rec__add-btn')?.addEventListener('click', async (e) => {
        const btn = /** @type {HTMLButtonElement} */ (e.currentTarget);
        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        btn.disabled = true;
        btn.innerHTML = '...';

        try {
          const sectionId = document.querySelector('cart-items-component')?.dataset.sectionId;
          const body = { id: variantId, quantity: 1 };
          if (sectionId) body.sections = sectionId;

          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await res.json();

          btn.innerHTML = '✓ Added';
          btn.classList.add('rec__add-btn--added');

          this.#isOwnEvent = true;
          document.dispatchEvent(new CustomEvent(CartAddEvent.eventName, {
            detail: { data }
          }));
          setTimeout(() => { this.#isOwnEvent = false; }, 500);

          setTimeout(() => {
            this.#fetchAndUpdate();
            this.#startObserving();
          }, 600);

          setTimeout(() => {
            btn.innerHTML = '<span>+</span> Add';
            btn.disabled = false;
            btn.classList.remove('rec__add-btn--added');
          }, 1500);

        } catch (err) {
          btn.innerHTML = '<span>+</span> Add';
          btn.disabled = false;
          this.#isOwnEvent = false;
          console.warn('[CartDrawer] 加车失败', err);
        }
      });
    }

    return el;
  }

  #renderRecCard() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog || !this.#recProducts.length) return;

    const el = dialog.querySelector('.cart-drawer__recommendations');
    if (!el) return;

    const p = this.#recProducts[this.#recIndex];
    const variant = p.variants?.find(v => v.available) ?? p.variants?.[0];
    const price = variant?.price ? `$${(parseFloat(variant.price) / 100).toFixed(2)}` : '';
    const variantId = variant?.id ?? '';
    const available = variant?.available ?? false;

    const rawImg = p.featured_image ?? p.images?.[0] ?? '';
    const img = rawImg ? this.#resizeShopifyImage(rawImg, 160) : '';

    const nextProduct = this.#recProducts[(this.#recIndex + 1) % this.#recProducts.length];
    if (nextProduct && nextProduct !== p) {
      const nextImg = nextProduct.featured_image ?? nextProduct.images?.[0] ?? '';
      if (nextImg) {
        const preload = new Image();
        preload.src = this.#resizeShopifyImage(nextImg, 160);
      }
    }

    const imgEl = el.querySelector('.rec__img');
    const imgLink = el.querySelector('.rec__img-link');
    const nameEl = el.querySelector('.rec__name');
    const priceEl = el.querySelector('.rec__price');
    const addBtn = /** @type {HTMLButtonElement} */ (el.querySelector('.rec__add-btn'));

    if (imgEl) { imgEl.src = img; imgEl.alt = p.title; }
    if (imgLink) imgLink.href = `/products/${p.handle}`;
    if (nameEl) { nameEl.textContent = p.title; nameEl.href = `/products/${p.handle}`; }
    if (priceEl) priceEl.textContent = price;
    if (addBtn) {
      addBtn.dataset.variantId = variantId;
      addBtn.disabled = !available;
      addBtn.innerHTML = available ? '<span>+</span> Add' : 'Sold out';
    }

    const prevBtn = el.querySelector('.rec__nav-prev');
    const nextBtn = el.querySelector('.rec__nav-next');
    if (prevBtn) prevBtn.style.opacity = this.#recProducts.length > 1 ? '1' : '0.3';
    if (nextBtn) nextBtn.style.opacity = this.#recProducts.length > 1 ? '1' : '0.3';
  }

  #renderRecommendations(products) {
    if (!products.length) {
      const { dialog } = /** @type {Refs} */ (this.refs);
      const el = dialog?.querySelector('.cart-drawer__recommendations');
      if (el) el.hidden = true;
      return;
    }

    this.#recProducts = products;
    this.#recIndex = 0;

    const el = this.#ensureRecommendationsEl();
    if (!el) return;

    el.hidden = false;
    this.#renderRecCard();
  }

  // ─── Shipping Fee + Our Total ──────────────────────────────────────────────

  #ensureShippingFeeEl() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return null;

    let el = dialog.querySelector('.cart-drawer__shipping-fee');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cart-drawer__shipping-fee';
      el.innerHTML = `
        <div class="cart-drawer__shipping-row cart__summary-item">
          <span class="cart-totals__label">Shipping</span>
          <span class="cart-totals__value cart-totals__shipping-value"></span>
        </div>
      `;

      // ✅ 隐藏主题的 estimated total 行，插入我们自己的
      const originalTotalRow = dialog.querySelector('.cart__summary-item.cart__total');
      if (originalTotalRow) {
        originalTotalRow.style.display = 'none';
        originalTotalRow.before(el);

        // ✅ 在 shipping 行后插入我们自己的 total 行，样式复用主题的 class
        const ourTotal = document.createElement('div');
        ourTotal.className = 'cart-drawer__our-total cart__summary-item cart__total';
        ourTotal.innerHTML = `
          <span class="cart__total-label cart-primary-typography">Estimated total</span>
          <span class="cart-drawer__our-total-value cart__total-value cart-secondary-typography"></span>
        `;
        el.after(ourTotal);
      }
    }

    return el;
  }

  #renderShippingFee(totalPriceCents) {
    if (typeof totalPriceCents !== 'number' || isNaN(totalPriceCents)) return;

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const el = this.#ensureShippingFeeEl();
    if (!el) return;

    const isFree = totalPriceCents >= this.#freeShippingThreshold;
    const shippingCents = isFree ? 0 : this.#shippingFee;
    const grandTotal = totalPriceCents + shippingCents;

    // ✅ 更新 shipping 行
    const shippingEl = el.querySelector('.cart-totals__shipping-value');
    if (shippingEl) {
      shippingEl.textContent = isFree ? 'FREE' : this.#formatMoney(shippingCents, 'USD');
      shippingEl.classList.toggle('cart-totals__shipping-value--free', isFree);
    }

    // ✅ 更新我们自己的 total 行，完全独立于主题的 text-component
    const ourTotalEl = dialog.querySelector('.cart-drawer__our-total-value');
    if (ourTotalEl) {
      ourTotalEl.textContent = this.#formatMoney(grandTotal, 'USD') + ' USD';
    }
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  #formatMoney(cents, currency = 'USD') {
    const amount = (cents / 100).toFixed(2);
    const symbols = { CNY: '¥', USD: '$', EUR: '€', GBP: '£', HKD: 'HK$' };
    return (symbols[currency] ?? currency + ' ') + amount;
  }

  #resizeShopifyImage(url, width) {
    if (!url) return '';
    const src = typeof url === 'object' ? (url.src ?? '') : url;
    if (!src) return '';
    return src.replace(/(\.[a-z]+)(\?.*)?$/, `_${width}x$1$2`);
  }

  // ─── Styles ────────────────────────────────────────────────────────────────

  #injectStyles() {
    if (document.getElementById('free-shipping-styles')) return;
    const style = document.createElement('style');
    style.id = 'free-shipping-styles';
    style.textContent = `
      cart-drawer-component .cart-drawer__free-shipping {
        flex-shrink: 0;
        padding: 16px 16px 20px;
        background-color: #F9F9F9;
        border-bottom: 1px solid rgba(128,128,128,0.15);
      }
      cart-drawer-component .free-shipping__message {
        font-size: 14px;
        font-weight: 500;
        color: #3D3B8E;
        text-align: center;
        margin: 0 0 10px;
      }
      cart-drawer-component .free-shipping__message strong {
        font-weight: 700;
      }
      cart-drawer-component .free-shipping__bar-wrap {
        position: relative;
        width: 100%;
        height: 8px;
        background-color: #E6E6E6;
        border-radius: 10px;
        overflow: visible;
      }
      cart-drawer-component .free-shipping__bar-fill {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        background-color: #B0E298;
        border-radius: 10px;
        width: 0%;
        transition: width 0.4s ease;
      }
      cart-drawer-component .free-shipping__bar-indicator {
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 16px;
        height: 16px;
        background-color: #3D3B8E;
        border: 2px solid #FFFFFF;
        border-radius: 50%;
        transition: left 0.4s ease;
        left: 0%;
        z-index: 2;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
      }
      cart-drawer-component scroll-hint.cart-drawer__content {
        display: block !important;
        overflow-y: auto !important;
        flex: 1 1 auto;
        min-height: 0;
      }
      cart-drawer-component scroll-hint.cart-drawer__items {
        display: block;
        overflow-y: unset;
        height: auto;
      }
      cart-drawer-component .cart-items__wrapper {
        padding-top: 20px;
        padding-bottom: 8px;
      }
      cart-drawer-component .cart-drawer__recommendations {
        padding: 20px 16px;
        border-top: 1px solid rgba(128,128,128,0.15);
      }
      cart-drawer-component .rec__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;
      }
      cart-drawer-component .rec__title {
        font-size: 14px;
        font-weight: 600;
        margin: 0;
        color: inherit;
      }
      cart-drawer-component .rec__nav {
        display: flex;
        gap: 4px;
      }
      cart-drawer-component .rec__nav-btn {
        width: 28px;
        height: 28px;
        border-radius: 4px;
        border: none;
        background: transparent;
        color: inherit;
        font-size: 22px;
        line-height: 1;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.4;
        transition: opacity 0.2s;
        padding: 0;
      }
      cart-drawer-component .rec__nav-btn:hover {
        opacity: 1;
      }
      cart-drawer-component .rec__card {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border-radius: 12px;
        background: rgba(128,128,128,0.06);
      }
      cart-drawer-component .rec__img-link {
        flex-shrink: 0;
        display: block;
        text-decoration: none;
      }
      cart-drawer-component .rec__img-wrap {
        width: 64px;
        height: 64px;
        border-radius: 8px;
        overflow: hidden;
        background: rgba(128,128,128,0.1);
      }
      cart-drawer-component .rec__img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: opacity 0.2s ease;
      }
      cart-drawer-component .rec__info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      cart-drawer-component .rec__name {
        font-size: 13px;
        font-weight: 500;
        color: inherit;
        text-decoration: none;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        line-height: 1.3;
      }
      cart-drawer-component .rec__price {
        font-size: 13px;
        font-weight: 600;
        color: inherit;
      }
      cart-drawer-component .rec__add-btn {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 8px 14px;
        border-radius: 8px;
        border: none;
        background: #3D3B8E;
        color: #fff;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        transition: opacity 0.2s, background 0.2s;
        white-space: nowrap;
        text-transform: none;
        letter-spacing: normal;
        width: auto;
        height: auto;
        line-height: normal;
      }
      cart-drawer-component .rec__add-btn span {
        font-size: 16px;
        line-height: 1;
      }
      cart-drawer-component .rec__add-btn:hover {
        opacity: 0.85;
      }
      cart-drawer-component .rec__add-btn--added {
        background: #B0E298;
        color: #2e6b1f;
      }
      cart-drawer-component .rec__add-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      cart-drawer-component .cart-drawer__shipping-row {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        padding: 4px 0;
      }
      cart-drawer-component .cart-totals__label {
        font-size: var(--font-size--2xs, 13px);
        font-weight: 400;
        color: rgb(var(--color-foreground-rgb) / var(--opacity-subdued-text, 0.65));
      }
      cart-drawer-component .cart-totals__value {
        font-size: var(--font-size--2xs, 13px);
        font-weight: 400;
        color: rgb(var(--color-foreground-rgb) / var(--opacity-subdued-text, 0.65));
      }
      cart-drawer-component .cart-totals__shipping-value--free {
        color: #B0E298;
        font-weight: var(--font-weight-bold, 600);
      }
      cart-drawer-component .cart-drawer__summary {
        position: static !important;
        overflow: visible !important;
      }
      cart-drawer-component .cart__summary-totals {
        padding-bottom: 24px;
      }
    `;
    document.head.appendChild(style);
  }

  // ─── Sticky State ──────────────────────────────────────────────────────────

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}